import React, { useState, useEffect } from 'react';
import { 
  Brain, MessageCircle, Video, Bot, Share2, LogOut, TrendingUp, 
  Zap, Sparkles, Crown, Phone, Shirt, Wand2, Film 
} from 'lucide-react';
import CommentMonitoring from './CommentMonitoring';
import LiveMonitoring from './LiveMonitoring';
import PostBoost from './PostBoost';
import SubscriptionPage from './SubscriptionPage';
import VideoAdCreator from './VideoAdCreator';
import MarketingAI from './MarketingAI';
import PageSelector from './PageSelector';
import MarketingStudio from './MarketingStudio';
import { API_URL } from '../../config';
import ProtectedMarketingStudio from './ProtectedMarketingStudio';
import CreditsPurchase from './CreditsPurchase';
import FacebookPageConnect from './FacebookPageConnect';

// Robot Vidéo Animé en cercle
const RobotVideo = () => (
  <div className="relative w-40 h-40 mx-auto">
    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
    
    <div className="relative z-10 w-40 h-40 rounded-full overflow-hidden border-4 border-gradient-to-br from-blue-500 via-purple-500 to-cyan-500 shadow-2xl">
      <video
        autoPlay
        loop
        muted
        playsInline
        className="w-full h-full object-cover"
      >
        <source src="/Teckia-face.mp4" type="video/mp4" />
      </video>
    </div>
    
    <div className="absolute inset-0 border-4 border-blue-500/30 rounded-full animate-ping" style={{animationDuration: '2s'}}></div>
    <div className="absolute inset-0 border-4 border-purple-500/30 rounded-full animate-ping" style={{animationDuration: '3s', animationDelay: '1s'}}></div>
    
    {[...Array(8)].map((_, i) => (
      <div
        key={i}
        className="absolute w-2 h-2 bg-blue-400/50 rounded-full animate-float"
        style={{
          top: `${Math.random() * 150}%`,
          left: `${Math.random() * 150}%`,
          animationDelay: `${i * 0.3}s`,
          animationDuration: `${3 + Math.random() * 2}s`
        }}
      />
    ))}
  </div>
);

// Background animé avec particles
const AnimatedBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-float"></div>
    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-float-delayed"></div>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
    <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
    {[...Array(20)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 bg-blue-400/30 rounded-full animate-float"
        style={{
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          animationDelay: `${i * 0.5}s`,
          animationDuration: `${5 + Math.random() * 5}s`
        }}
      />
    ))}
  </div>
);

// Service Card
const ServiceCard = ({ icon: Icon, title, description, isActive, onClick, color, badge }) => (
  <div 
    onClick={onClick}
    className={`group relative overflow-hidden rounded-3xl p-6 md:p-8 cursor-pointer transition-all duration-500 hover:scale-105 hover:-translate-y-2 ${
      isActive 
        ? `bg-gradient-to-br ${color} shadow-2xl border-2 border-white/30`
        : 'bg-white/5 hover:bg-white/10 backdrop-blur-xl border-2 border-white/10 hover:border-white/20'
    }`}
  >
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
    <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br ${color} blur-2xl`}></div>
    
    <div className="relative z-10">
      <div className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl ${isActive ? 'bg-white/20' : 'bg-white/10'} flex items-center justify-center mb-4 md:mb-6 group-hover:rotate-12 group-hover:scale-110 transition-all duration-500`}>
        <Icon className="w-8 h-8 md:w-10 md:h-10 text-white" />
      </div>
      
      <h3 className="text-lg md:text-2xl font-bold text-white mb-2">{title}</h3>
      <p className="text-blue-200 mb-4 md:mb-6 text-xs md:text-sm">{description}</p>
      
      {badge && (
        <div className="mb-3">
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
            {badge}
          </span>
        </div>
      )}
      
      <div className={`inline-flex items-center gap-2 px-3 md:px-4 py-2 rounded-full text-xs md:text-sm font-medium ${
        isActive 
          ? 'bg-green-500/20 text-green-300 border border-green-500/30'
          : 'bg-white/10 text-white/60'
      }`}>
        <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-400 animate-pulse' : 'bg-white/40'}`}></div>
        {isActive ? 'Actif' : 'Non abonné'}
      </div>
    </div>
  </div>
);

// Message "Pas d'abonnement"
const NoSubscriptionMessage = ({ onSubscribe, onLogout }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 relative overflow-hidden">
    <AnimatedBackground />
    
    <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl sticky top-0 z-40">
      <div className="container mx-auto px-4 py-4 md:py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/TeckiA-logo.gif" 
              alt="TeckiA IA" 
              className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
            />
            <div>
              <h1 className="text-base md:text-xl font-bold text-white flex items-center gap-2">
                TeckiA IA
                <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-yellow-400 animate-pulse" />
              </h1>
              <p className="text-xs md:text-sm text-blue-200">
                Bonjour, {localStorage.getItem('user_name')}
              </p>
            </div>
          </div>
          
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-all hover:scale-105 text-sm md:text-base"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Déconnexion</span>
          </button>
        </div>
      </div>
    </header>
    
    <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-80px)] p-4 md:p-8">
      <div className="max-w-4xl w-full text-center">
        <RobotVideo />
        
        <h2 className="text-4xl md:text-6xl font-bold mt-8 mb-4">
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
            Salut, TeckiA est à ton service  
          </span>
        </h2>
        
        <p className="text-lg md:text-2xl text-purple-200 mb-8">
          Aucun service actif pour le moment
        </p>
        
        <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 md:p-12 border border-white/10 mb-8">
          <Crown className="w-16 h-16 md:w-20 md:h-20 text-yellow-400 mx-auto mb-6 animate-bounce" />
          
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Découvrez nos services intelligents
          </h3>
          
          <p className="text-base md:text-lg text-white/70 mb-8 max-w-2xl mx-auto">
            Automatisez vos réponses, gérez vos conversations, boostez vos ventes avec l'intelligence artificielle
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="text-3xl mb-2">🤖</div>
              <div className="text-white font-medium text-sm md:text-base">Bot IA Intelligent</div>
            </div>
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="text-3xl mb-2">💬</div>
              <div className="text-white font-medium text-sm md:text-base">Surveillance Auto</div>
            </div>
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="text-3xl mb-2">🎨</div>
              <div className="text-white font-medium text-sm md:text-base">Marketing IA</div>
            </div>
          </div>
          
          <button
            onClick={onSubscribe}
            className="px-8 md:px-12 py-4 md:py-6 rounded-2xl bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 hover:from-purple-700 hover:via-pink-700 hover:to-cyan-700 text-white font-bold text-base md:text-xl transition-all hover:scale-110 shadow-2xl animate-pulse"
          >
            <span className="flex items-center gap-3 justify-center">
              <Sparkles className="w-5 h-5 md:w-6 md:h-6" />
              Voir les abonnements
              <Sparkles className="w-5 h-5 md:w-6 md:h-6" />
            </span>
          </button>
        </div>
        
        <p className="text-sm md:text-base text-white/40">
          Essai gratuit disponible • Sans engagement • Support 24/7
        </p>
      </div>
    </div>
  </div>
);

export default function ClientDashboard({ onLogout }) {
  const [view, setView] = useState('dashboard');
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [activePagesCount, setActivePagesCount] = useState({
    teckia_vtryon: 0,
    teckia_universal: 0,
    messenger_bot: 0,
    comment_monitoring: 0
  });
  const [creditsSummary, setCreditsSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const ALL_SERVICES = [
    // 'Messenger Bot IA' supprimé : remplacé par « Page Facebook »
    // (connexion officielle + agent IA + ventes + conversations + prise de main)
    {
      id: 'video_ads',
      icon: Video,
      title: 'TeckiA Pub Vidéos',
      description: 'Vidéos publicitaires IA 30s',
      color: 'from-red-600 to-orange-600',
      component: 'video_ads',
      badge: '🎬 Nouveau'
    },
    {
      id: 'marketing_studio',
      icon: Film,
      title: 'Marketing-Studio',
      description: 'Images Pub & Vidéos',
      color: 'from-purple-600 to-pink-600',
      component: 'ambondrombe'
    },
    {
      id: 'teckia_vtryon',
      icon: Shirt,
      title: 'TeckiA VTry-on',
      description: 'Essayage virtuel mode IA',
      color: 'from-pink-600 to-rose-600',
      component: 'vtryon',
      badge: 'Fashion'
    },
    {
      id: 'teckia_universal',
      icon: Wand2,
      title: 'Universal Marketing',
      description: 'Marketing produits universel',
      color: 'from-cyan-600 to-blue-600',
      component: 'universal',
      badge: 'All Products'
    },
    {
      id: 'comment_monitoring',
      icon: MessageCircle,
      title: 'Surveillance Commentaires',
      description: 'Détection et réponse auto',
      color: 'from-orange-600 to-red-600',
      component: 'comment_monitoring'
    },
    {
      id: 'whatsapp_bot',
      icon: MessageCircle,
      title: 'WhatsApp Bot IA',
      description: 'Conversations intelligentes WhatsApp',
      color: 'from-green-600 to-emerald-600',
      component: 'whatsapp'
    },
    {
      id: 'ai_agent_alone',
      icon: Phone,
      title: 'Agent IA Réceptionniste',
      description: 'Répondeur vocal intelligent',
      color: 'from-purple-600 to-violet-600',
      component: 'ai_agent'
    },
    {
      id: 'ai_agent_website',
      icon: Brain,
      title: 'Agent IA + Site Web',
      description: 'Réceptionniste + Site vitrine',
      color: 'from-indigo-600 to-purple-600',
      component: 'ai_agent_web'
    }
  ];

  useEffect(() => {
    loadSubscriptions();
    loadActivePagesCount();
    loadCreditsSummary();
  }, []);

  const loadCreditsSummary = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bot/credits-summary`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setCreditsSummary(data.credits);
      }
    } catch (e) {
      console.error('Erreur credits summary:', e);
    }
  };

  // Badge crédits affiché sur les cartes des modules à crédits
  const creditsBadge = (serviceId) => {
    if (!creditsSummary || !creditsSummary[serviceId]) return '💰 0 crédit';
    const c = creditsSummary[serviceId];
    const parts = [];
    if (c.images !== undefined) parts.push(`📸 ${c.images} img`);
    if (c.videos !== undefined) parts.push(`🎬 ${c.videos} vid`);
    const total = (c.images || 0) + (c.videos || 0);
    return total > 0 ? parts.join(' · ') : '💰 0 crédit — Achetez';
  };

  const loadSubscriptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/subscriptions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📦 Subscriptions received:', data);
        
        const subs = data.subscriptions || [];
        
        const activeSubs = subs.filter(sub => {
          if (sub.status === 'active' && sub.setup_status === 'completed') {
            return true;
          }
          if (
            sub.service_id === 'teckia_vtryon' || 
            sub.service_id === 'teckia_universal' ||
            sub.service_id === 'video_ads' ||        // ✅ AJOUTÉ
            sub.service_id === 'marketing_studio'    // ✅ AJOUTÉ
          ) {
            return sub.status === 'active';
          }
          return false;
        });
        
        console.log('✅ Active subscriptions:', activeSubs);
        setActiveSubscriptions(activeSubs);
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivePagesCount = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const pages = data.clients || [];
        
        const counts = {
          teckia_vtryon: 0,
          teckia_universal: 0,
          messenger_bot: 0,
          comment_monitoring: 0
        };
        
        pages.forEach(page => {
          if (
            (page.max_images_studiopro > 0) ||
            (page.max_videos_wan > 0) ||
            (page.max_videos_hailua > 0)
          ) {
            counts.teckia_vtryon++;
          }
          
          if (
            (page.max_images_universal > 0) ||
            (page.max_videos_universal > 0)
          ) {
            counts.teckia_universal++;
          }
          
          counts.messenger_bot++;
        });
        
        try {
          const monitoringRes = await fetch(`${API_URL}/api/comment-monitoring/active-pages`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
          });
          if (monitoringRes.ok) {
            const monitoringData = await monitoringRes.json();
            counts.comment_monitoring = monitoringData.count || 0;
          }
        } catch (err) {
          console.log('⚠️ Comment monitoring count not available:', err);
        }
        
        console.log('📊 Active pages count:', counts);
        setActivePagesCount(counts);
      }
    } catch (error) {
      console.error('Error loading pages:', error);
    }
  };

  // Modules 100% CRÉDITS : toujours visibles, utilisables selon les crédits achetés
  const CREDIT_SERVICES = ['video_ads', 'marketing_studio', 'teckia_vtryon'];

  const isServiceActive = (serviceId) => {
    // Modèle crédits : ces modules sont toujours affichés (0 crédit au départ)
    if (CREDIT_SERVICES.includes(serviceId)) return true;
    
    const hasSub = activeSubscriptions.some(sub => sub.service_id === serviceId);
    
    console.log(`🔍 isServiceActive(${serviceId}):`, {
      hasSub,
      activePagesCount: activePagesCount[serviceId],
      subscriptions: activeSubscriptions.filter(s => s.service_id === serviceId)
    });
    
    if (serviceId === 'teckia_vtryon' || serviceId === 'teckia_universal') {
      const isActive = hasSub && (activePagesCount[serviceId] > 0);
      console.log(`✅ ${serviceId} active:`, isActive);
      return isActive;
    }
    
    if (serviceId === 'comment_monitoring') {
      const isActive = hasSub && (activePagesCount.comment_monitoring > 0);
      console.log(`✅ ${serviceId} active:`, isActive);
      return isActive;
    }
    
    console.log(`✅ ${serviceId} active:`, hasSub);
    return hasSub;
  };

  const activeServices = ALL_SERVICES.filter(s => isServiceActive(s.id));


  if (view === 'ambondrombe') {
    return <ProtectedMarketingStudio onBack={() => setView('dashboard')} />;
  }

  if (view === 'subscription') {
    return <SubscriptionPage onBack={() => { setView('dashboard'); loadSubscriptions(); }} />;
  }

  if (view === 'credits') {
    return <CreditsPurchase onBack={() => { setView('dashboard'); loadSubscriptions(); }} />;
  }

  if (view === 'video_ads') {
    return <VideoAdCreator onBack={() => setView('dashboard')} />;
  }

  if (view === 'vtryon' || view === 'universal') {
    return <PageSelector serviceType={view} onBack={() => setView('dashboard')} />;
  }

  if (view === 'comment_monitoring') {
    return <PageSelector serviceType="comment_monitoring" onBack={() => setView('dashboard')} />;
  }

  if (view === 'fb_connect') return <FacebookPageConnect onBack={() => setView('dashboard')} />;
  if (view === 'live') return <LiveMonitoring onBack={() => setView('dashboard')} />;
  if (view === 'boost') return <PostBoost onBack={() => setView('dashboard')} />;
  if (view === 'marketing') return <MarketingAI onBack={() => setView('dashboard')} />;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-white text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  if (activeServices.length === 0) {
    return <NoSubscriptionMessage onSubscribe={() => setView('subscription')} onLogout={onLogout} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 relative overflow-hidden">
      <AnimatedBackground />
      
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img 
                src="/TeckiA-logo.gif" 
                alt="TeckiA IA" 
                className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
              />
              <div>
                <img 
                  src="/name_teckia.png" 
                  alt="TeckiA" 
                  className="w-24 h-10 md:w-32 md:h-12 object-contain"
                />
                <p className="text-xs md:text-sm text-blue-200">
                  Bonjour, {localStorage.getItem('user_name')}
                </p>
              </div>
            </div>
            
            <div className="flex gap-2">

                    <button
          onClick={() => setView('credits')}
          className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20 hover:from-yellow-500/30 hover:to-orange-500/30 text-yellow-300 border border-yellow-500/30 transition-all hover:scale-105 text-sm md:text-base group"
        >
          <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
          <span className="hidden sm:inline">Acheter Crédits</span>
          <span className="sm:hidden">Crédits</span>
        </button>

              <button
                onClick={() => setView('subscription')}
                className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 transition-all hover:scale-105 text-sm md:text-base"
              >
                <Crown className="w-4 h-4" />
                <span className="hidden sm:inline">Abonnements</span>
              </button>
              
              <button
                onClick={onLogout}
                className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-all hover:scale-105 text-sm md:text-base"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 md:py-12 relative z-10">
        <div className="text-center mb-8 md:mb-12 relative">
          <RobotVideo />
          
          <h2 className="text-3xl md:text-6xl font-bold mt-6 md:mt-8 mb-3 md:mb-4">
            😎 <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Salut, c'est TeckiA  
            </span> 🤖
          </h2>
          <p className="text-base md:text-2xl text-blue-200 mb-2">
            {activeServices.length} service{activeServices.length > 1 ? 's' : ''} actif{activeServices.length > 1 ? 's' : ''}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 mb-8 md:mb-12">
          {/* Connexion Page Facebook — connexion, agent IA, ventes, conversations, alertes */}
          <ServiceCard
            id="fb_connect"
            icon={Share2}
            title="Page Facebook"
            description="Agent IA, ventes, conversations et alertes de votre page"
            color="from-blue-600 to-cyan-600"
            badge="🔗 Connexion officielle"
            isActive={true}
            onClick={() => setView('fb_connect')}
          />
          {activeServices.map((service) => (
            <ServiceCard
              key={service.id}
              {...service}
              badge={CREDIT_SERVICES.includes(service.id) ? creditsBadge(service.id) : service.badge}
              isActive={true}
              onClick={() => setView(service.component)}
            />
          ))}
        </div>

        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-6 md:p-8 text-white text-center">
          <Crown className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 animate-bounce" />
          <h3 className="text-xl md:text-2xl font-bold mb-2">Découvrez d'autres services</h3>
          <p className="text-sm md:text-base text-white/80 mb-6">
            Boostez votre productivité avec nos outils IA
          </p>
          <button
            onClick={() => setView('subscription')}
            className="px-6 md:px-8 py-3 md:py-4 rounded-xl bg-white/20 hover:bg-white/30 font-bold text-sm md:text-base transition-all hover:scale-105"
          >
            Voir tous les abonnements
          </button>
        </div>
      </main>
    </div>
  );
}